import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { safeCopyToClipboard } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Check, X, Gift, Share2, Home, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function OneOffSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  
  const sessionId = searchParams.get("session_id");
  const interactionId = searchParams.get("interaction_id");

  useEffect(() => {
    if (sessionId) {
      completePayment();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const completePayment = async () => {
    try {
      const response = await apiClient.post(`/payments/one-off/complete?session_id=${sessionId}`);
      setPaymentData(response.data);
      setStatus("success");
    } catch (error) {
      // Check if payment was already completed
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("already completed")) {
        setStatus("success");
        setPaymentData({ alreadyProcessed: true });
      } else {
        setStatus("failed");
        toast.error(error.response?.data?.detail || "Payment verification failed");
      }
    }
  };

  // Loading State
  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="oneoff-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  // Failed State
  if (status === "failed") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="oneoff-failed">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="font-heading font-black text-2xl text-slate-900 mb-3">
            Payment Not Completed
          </h1>
          <p className="text-slate-500 mb-6">
            There was an issue processing your payment. Your card was not charged.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate(`/form/${interactionId}?type=one_off`)}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="try-again-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="w-full h-12"
            >
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-6" data-testid="oneoff-success">
      <div className="max-w-md text-center">
        <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-[bounce_1s_ease-in-out]">
          <Check className="h-12 w-12 text-emerald-600" />
        </div>
        
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-full mb-4">
            <Gift className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">One-Off Donation</span>
          </div>
          
          <h1 className="font-heading font-black text-3xl text-slate-900 mb-3">
            Thank You!
          </h1>
          <p className="text-slate-600">
            Your generous donation has been received. Your support makes a real difference!
          </p>
        </div>

        {paymentData?.amount && (
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 mb-6">
            <div className="text-sm text-slate-500 mb-1">Donation Amount</div>
            <div className="font-heading font-black text-4xl text-emerald-600">
              £{(paymentData.amount / 100).toFixed(2)}
            </div>
          </div>
        )}

        <div className="bg-emerald-50 rounded-xl p-6 mb-6 text-left">
          <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Spread the Word
          </h3>
          <p className="text-sm text-emerald-700 mb-4">
            Know someone who might want to help? Share the campaign with friends and family!
          </p>
          <Button
            variant="outline"
            className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            onClick={async () => {
              const ok = await safeCopyToClipboard(window.location.origin);
              toast.success(ok ? "Link copied to clipboard!" : "Copy failed — select manually");
            }}
            data-testid="share-btn"
          >
            Copy Campaign Link
          </Button>
        </div>

        <Button
          onClick={() => navigate("/")}
          className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="done-btn"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
